import React, { useState, useEffect, useContext } from "react";
import { useFirestore } from "../../firebase/FirestoreContext";
import { collection, addDoc, getDocs, doc, updateDoc, arrayUnion } from "firebase/firestore";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import config from "../../../config";
import { EnvContext } from "../../../context/EnvContext";

const NewProduct = () => {
  const environment = useContext(EnvContext);
  const navigate = useNavigate();
  const db = useFirestore();

  // Determine the base path based on the environment
  const collectionPath = environment === "staging" ? config.enveironment.staging.collectionPath : config.enveironment.production.collectionPath;

  // Expanded initial state (matching EditProduct)
  const [productData, setProductData] = useState({
    product_uid: "",
    brand_uid: "",
    product_name: "",
    product_description: "",
    potency_value: "0",
    key_ingredients: "",
    tac: "",
    weight_units: "",
    net_weight: "0",
    product_category: "",
    strain_type: "",
    effects: "",
    strength: "",
    product_type: "",
    number_servings: 0,
    serving_size: "",
    dose_per_serving: 0,
    dosing_guide: "",
    images: [],
    perishable: false,
    inactive: false,
    rating_initial: {
      rating: 0,
      voting: 0,
    },
    tags: [],
    subcategories: [],
    vibes: [],
    staff_pick: false,
    popular_product: false,
    order_category_row: 0,
    order_category_page: 0,
  });

  const [brands, setBrands] = useState([]);
  const [dosingGuides, setDosingGuides] = useState([]);
  const [categoryTags, setCategoryTags] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [vibesList, setVibesList] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState([]);
  const [selectedVibes, setSelectedVibes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newProductUID, setNewProductUID] = useState(null);

  // Options from config
  const effectsOptions = config.effectsOptions;
  const productTypeOptions = config.productTypeOptions;
  const productCategoryOptions = config.productCategoryOptions;
  const weightUnitsOptions = config.weightUnitsOptions;
  const strain_typeOptions = config.strain_typeOptions;
  const dosingGuideOptions = dosingGuides; // Already fetched

  // Fetch Brands and Dosing Guides on mount and generate new product UID
  useEffect(() => {
    const fetchBrands = async () => {
      const brandsCollectionRef = collection(db, `${collectionPath}Brands/BrandsList/DataBase`);
      const querySnapshot = await getDocs(brandsCollectionRef);
      const brandsArray = [];
      querySnapshot.forEach((doc) => {
        brandsArray.push({ id: doc.id, ...doc.data() });
      });
      setBrands(brandsArray);
    };

    const fetchDosingGuides = async () => {
      const dosingGuidesCollectionRef = collection(db, `${collectionPath}DosingGuides/DosingGuidesList/DataBase`);
      const querySnapshot = await getDocs(dosingGuidesCollectionRef);
      const guidesArray = [];
      querySnapshot.forEach((doc) => {
        guidesArray.push(doc.data().name);
      });
      setDosingGuides(guidesArray);
    };

    const getNewProductUID = async () => {
      const productsCollectionRef = collection(db, `${collectionPath}Products/ProductsList/DataBase`);
      const productsSnapshot = await getDocs(productsCollectionRef);
      let maxUID = 0;
      productsSnapshot.forEach((doc) => {
        const data = doc.data();
        const uid = parseInt(data.product_uid, 10);
        if (uid > maxUID) {
          maxUID = uid;
        }
      });
      setNewProductUID(maxUID + 1);
      // Also update productData with the new UID
      setProductData((prev) => ({ ...prev, product_uid: maxUID + 1 }));
    };

    fetchBrands();
    fetchDosingGuides();
    getNewProductUID();
  }, [db, collectionPath]);

  // Fetch category tags and subcategories when product_category changes
  useEffect(() => {
    const fetchCategoryData = async () => {
      if (productData.product_category) {
        const categoriesCollectionRef = collection(db, `${collectionPath}SooperstockSettings/ProductCategories/DataBase`);
        const querySnapshot = await getDocs(categoriesCollectionRef);
        const categoryDoc = querySnapshot.docs.find((doc) => doc.data().name === productData.product_category);

        if (categoryDoc) {
          const data = categoryDoc.data();
          setCategoryTags(data.tags || []);
          setSubcategories(data.subCategories || []);
          setSelectedSubcategory((prev) => prev.filter((sub) => data.subCategories?.includes(sub)));
        } else {
          setCategoryTags([]);
          setSubcategories([]);
          setSelectedSubcategory([]);
        }
      } else {
        setCategoryTags([]);
        setSubcategories([]);
        setSelectedSubcategory([]);
      }
    };
    fetchCategoryData();
    setSelectedTags([]);
  }, [productData.product_category, db, collectionPath]);

  useEffect(() => {
    const fetchTags = async () => {
      const tagsCollectionRef = collection(db, `${collectionPath}SooperstockSettings/ProductTags/DataBase`);
      const querySnapshot = await getDocs(tagsCollectionRef);

      const tagsArray = [];
      querySnapshot.forEach((doc) => {
        tagsArray.push(doc.data().name);
      });
      setCategoryTags(tagsArray);
    };
    fetchTags();
  }, [productData.product_category, db, collectionPath]);

  // Fetch vibes list (from a dedicated collection)
  useEffect(() => {
    const fetchVibes = async () => {
      const vibesCollectionRef = collection(db, `${collectionPath}SooperstockSettings/ProductVibes/DataBase`);
      const querySnapshot = await getDocs(vibesCollectionRef);
      const vibesArray = [];
      querySnapshot.forEach((doc) => {
        vibesArray.push(doc.data().name);
      });
      setVibesList(vibesArray);
    };
    fetchVibes();
  }, [db, collectionPath]);

  // Utility function to format UID with a prefix "P"
  const formatUID = (uid, totalLength = 8) => {
    return uid ? `P${String(uid).padStart(totalLength, "0")}` : "";
  };

  // Toggle functions for tags, subcategories, and vibes
  const handleTagToggle = (tag) => {
    setSelectedTags((prevTags) => (prevTags.includes(tag) ? prevTags.filter((t) => t !== tag) : [...prevTags, tag]));
  };

  const handleSubcategoryToggle = (subcategory) => {
    setSelectedSubcategory((prev) => (prev.includes(subcategory) ? prev.filter((s) => s !== subcategory) : [...prev, subcategory]));
  };

  const handleVibesToggle = (vibe) => {
    setSelectedVibes((prev) => (prev.includes(vibe) ? prev.filter((v) => v !== vibe) : [...prev, vibe]));
  };

  // Update Brand and Products Meta data (similar to EditProduct)
  const updateBrandData = async (brand_uid, category, effects, productType) => {
    try {
      const brandDocRef = doc(db, `${collectionPath}Brands/BrandsList/DataBase`, brand_uid);
      await updateDoc(brandDocRef, {
        brand_category: arrayUnion(category),
        effects: arrayUnion(effects),
        product_type: arrayUnion(productType),
      });
    } catch (error) {
      console.error("Error updating brand data:", error);
    }
  };

  const updateProductsMetaData = async (category, effects, productType) => {
    try {
      const productsMetaDocRef = doc(db, `${collectionPath}Products/ProductsMeta`);
      await updateDoc(productsMetaDocRef, {
        categories: arrayUnion(category),
        effects: arrayUnion(effects),
        product_type: arrayUnion(productType),
      });
    } catch (error) {
      console.error("Error updating products meta data:", error);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const productsCollectionRef = collection(db, `${collectionPath}Products/ProductsList/DataBase`);
    try {
      const selectedBrand = brands.find((b) => b.id === productData.brand_uid);
      if (!selectedBrand) {
        console.error("Selected brand not found.");
        toast.error("Selected brand not found."); // Provide user feedback
        setLoading(false);
        return;
      }

      const newProduct = {
        product_uid: productData.product_uid,
        brand_uid: selectedBrand.brand_uid,
        brand_name: selectedBrand.brand_name,
        product_name: productData.product_name,
        product_description: productData.product_description || "",
        potency_value: Number(productData.potency_value) || 0,
        key_ingredients: productData.key_ingredients || "",
        tac: productData.tac || "",
        weight_units: productData.weight_units || "",
        net_weight: productData.net_weight || "0",
        product_category: productData.product_category || "",
        strain_type: productData.strain_type || "",
        effects: productData.effects || "",
        strength: productData.strength || "",
        product_type: productData.product_type || "",
        number_servings: Number(productData.number_servings) || 0,
        serving_size: productData.serving_size || "",
        dose_per_serving: Number(productData.dose_per_serving) || 0,
        images: productData.images || [],
        perishable: productData.perishable,
        inactive: productData.inactive || false,
        rating_initial: {
          rating: productData.rating_initial?.rating || 0,
          voting: productData.rating_initial?.voting || 0,
        },
        tags: selectedTags,
        subcategories: selectedSubcategory,
        vibes: selectedVibes,
        staff_pick: productData.staff_pick || false,
        popular_product: productData.popular_product || false,
        order_category_row: Number(productData.order_category_row) || 0,
        order_category_page: Number(productData.order_category_page) || 0,
      };

      // Add the new product document
      const newDocRef = await addDoc(productsCollectionRef, newProduct);

      // Update brand and products meta data
      await updateBrandData(productData.brand_uid, productData.product_category, productData.effects, productData.product_type);
      await updateProductsMetaData(productData.product_category, productData.effects, productData.product_type);
      toast.success("Product added successfully");

      // Redirect to the edit page of the new product
      navigate(`/main/products/edit/${newDocRef.id}`);
    } catch (error) {
      console.error("Error adding product:", error);
      toast.error("Error adding product: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <ToastContainer position="top-center" autoClose={5000} />
      <h2>Insert New Product</h2>
      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={{ padding: "20px" }}>
          {/* Product UID */}
          <div style={styles.formElement}>
            <label style={styles.formLabel}>Product UID:</label>
            <input type="text" value={formatUID(productData.product_uid)} style={styles.formInput} disabled />
          </div>

          {/* Brand */}
          <div style={styles.formElement}>
            <label style={styles.formLabel}>Brand&#42;:</label>
            <select value={productData.brand_uid} onChange={(e) => setProductData({ ...productData, brand_uid: e.target.value })} style={styles.formSelect}>
              <option value=""></option>
              {brands
                .slice()
                .sort((a, b) => a.brand_name.localeCompare(b.brand_name))
                .map((brand) => (
                  <option key={brand.id} value={brand.id}>
                    {brand.brand_name}
                  </option>
                ))}
            </select>
          </div>

          {/* Name */}
          <div style={styles.formElement}>
            <label style={styles.formLabel}>Name&#42;:</label>
            <input
              type="text"
              value={productData.product_name}
              onChange={(e) => setProductData({ ...productData, product_name: e.target.value })}
              style={styles.formInput}
            />
          </div>

          {/* Description */}
          <div style={styles.formElement}>
            <label style={styles.formLabel}>Description:</label>
            <textarea
              value={productData.product_description}
              onChange={(e) => setProductData({ ...productData, product_description: e.target.value })}
              style={styles.formInput}
            ></textarea>
          </div>

          {/* Strain Type */}
          <div style={styles.formElement}>
            <label style={styles.formLabel}>Strain Type:</label>
            <select value={productData.strain_type} onChange={(e) => setProductData({ ...productData, strain_type: e.target.value })} style={styles.formSelect}>
              <option value=""></option>
              {strain_typeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {/* Effects */}
          <div style={styles.formElement}>
            <label style={styles.formLabel}>Effects:</label>
            <select value={productData.effects} onChange={(e) => setProductData({ ...productData, effects: e.target.value })} style={styles.formSelect}>
              <option value=""></option>
              {effectsOptions.map((effect) => (
                <option key={effect} value={effect}>
                  {effect}
                </option>
              ))}
            </select>
          </div>

          {/* Strength */}
          <div style={styles.formElement}>
            <label style={styles.formLabel}>Strength:</label>
            <input
              type="text"
              value={productData.strength}
              onChange={(e) => setProductData({ ...productData, strength: e.target.value })}
              style={styles.formInput}
            />
          </div>

          {/* Product Type */}
          <div style={styles.formElement}>
            <label style={styles.formLabel}>Product Type:</label>
            <select
              value={productData.product_type}
              onChange={(e) => setProductData({ ...productData, product_type: e.target.value })}
              style={styles.formSelect}
            >
              <option value=""></option>
              {productTypeOptions.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Product Category */}
          <div style={styles.formElement}>
            <label style={styles.formLabel}>Product Category:</label>
            <select
              value={productData.product_category}
              onChange={(e) => setProductData({ ...productData, product_category: e.target.value })}
              style={styles.formSelect}
            >
              <option value=""></option>
              {productCategoryOptions.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {/* Subcategories */}
          <div style={styles.formElement}>
            <label style={styles.formLabel}>Product Subcategories:</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "10px" }}>
              {subcategories.length ? (
                subcategories.map((sub, index) => (
                  <span
                    key={index}
                    onClick={() => handleSubcategoryToggle(sub)}
                    style={{
                      padding: "6px 12px",
                      borderRadius: "25px",
                      cursor: "pointer",
                      backgroundColor: selectedSubcategory.includes(sub) ? "#478169" : "#ccc",
                      color: selectedSubcategory.includes(sub) ? "white" : "black",
                      fontSize: 12,
                    }}
                  >
                    {sub}
                  </span>
                ))
              ) : (
                <span style={{ opacity: 0.5 }}>No subcategories available</span>
              )}
            </div>
          </div>

          {/* Tags */}
          <div style={styles.formElement}>
            <label style={styles.formLabel}>Product Tags:</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
              {categoryTags.length ? (
                categoryTags.map((tag, index) => (
                  <span
                    key={index}
                    onClick={() => handleTagToggle(tag)}
                    style={{
                      padding: "6px 12px",
                      borderRadius: "25px",
                      cursor: "pointer",
                      backgroundColor: selectedTags.includes(tag) ? "#478169" : "#ccc",
                      color: selectedTags.includes(tag) ? "white" : "black",
                      fontSize: 12,
                    }}
                  >
                    {tag}
                  </span>
                ))
              ) : (
                <span style={{ opacity: 0.5 }}>No tags available</span>
              )}
            </div>
          </div>

          {/* Vibes */}
          <div style={styles.formElement}>
            <label style={styles.formLabel}>Product Vibes:</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
              {vibesList.length ? (
                vibesList.map((vibe, index) => (
                  <span
                    key={index}
                    onClick={() => handleVibesToggle(vibe)}
                    style={{
                      padding: "6px 12px",
                      borderRadius: "25px",
                      cursor: "pointer",
                      backgroundColor: selectedVibes.includes(vibe) ? "#478169" : "#ccc",
                      color: selectedVibes.includes(vibe) ? "white" : "black",
                      fontSize: 12,
                    }}
                  >
                    {vibe}
                  </span>
                ))
              ) : (
                <span style={{ opacity: 0.5 }}>No vibes available</span>
              )}
            </div>
          </div>

          {/* Key Ingredients */}
          <div style={styles.formElement}>
            <label style={styles.formLabel}>Key Ingredients:</label>
            <input
              type="text"
              value={productData.key_ingredients}
              onChange={(e) => setProductData({ ...productData, key_ingredients: e.target.value })}
              style={styles.formInput}
            />
          </div>

          {selectedTags.some(tag => String(tag).toLowerCase() === "thc") && (
            <div style={styles.formElement}>
              <label style={styles.formLabel}>TAC:</label>
              <input
                type="text"
                value={productData.tac}
                onChange={(e) =>
                  setProductData({
                    ...productData,
                    tac: e.target.value,
                  })
                }
                style={styles.formInput}
              />
            </div>
          )}
          {/* Net Weight and Weight Unit */}
          <div style={{ display: "flex", flexDirection: "row" }}>
            <div style={{ ...styles.formElement, marginRight: 20 }}>
              <label style={styles.formLabel}>Net Weight:</label>
              <input
                type="text"
                value={productData.net_weight}
                onChange={(e) => setProductData({ ...productData, net_weight: e.target.value })}
                style={styles.formInput}
              />
            </div>
            <div style={styles.formElement}>
              <label style={styles.formLabel}>Weight Unit:</label>
              <select
                value={productData.weight_units}
                onChange={(e) => setProductData({ ...productData, weight_units: e.target.value })}
                style={styles.formSelect}
              >
                <option value=""></option>
                {weightUnitsOptions.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Potency */}
          <div style={styles.formElement}>
            <label style={styles.formLabel}>Potency:</label>
            <input
              type="text"
              value={productData.potency_value}
              onChange={(e) => setProductData({ ...productData, potency_value: e.target.value })}
              style={styles.formInput}
            />
          </div>

          {/* Number of Servings */}
          <div style={styles.formElement}>
            <label style={styles.formLabel}>Number of Servings:</label>
            <input
              type="text"
              value={productData.number_servings}
              onChange={(e) => setProductData({ ...productData, number_servings: e.target.value })}
              style={styles.formInput}
            />
          </div>

          {/* Serving Size */}
          <div style={styles.formElement}>
            <label style={styles.formLabel}>Serving Size:</label>
            <input
              type="text"
              value={productData.serving_size}
              onChange={(e) => setProductData({ ...productData, serving_size: e.target.value })}
              style={styles.formInput}
            />
          </div>

          {/* Dose per Serving */}
          <div style={styles.formElement}>
            <label style={styles.formLabel}>Dose per Serving:</label>
            <input
              type="text"
              value={productData.dose_per_serving}
              onChange={(e) => setProductData({ ...productData, dose_per_serving: e.target.value })}
              style={styles.formInput}
            />
          </div>

          {/* Dosing Guide */}
          <div style={styles.formElement}>
            <label style={styles.formLabel}>Dosing Guide:</label>
            <select
              value={productData.dosing_guide}
              onChange={(e) => setProductData({ ...productData, dosing_guide: e.target.value })}
              style={styles.formSelect}
            >
              <option value=""></option>
              {dosingGuideOptions.map((guide) => (
                <option key={guide} value={guide}>
                  {guide}
                </option>
              ))}
            </select>
          </div>

          {/* Perishable */}
          <div style={{ ...styles.formElement, display: "flex", flexDirection: "row" }}>
            <label style={styles.formLabel}>Perishable:</label>
            <input
              type="checkbox"
              checked={productData.perishable}
              onChange={(e) => setProductData({ ...productData, perishable: e.target.checked })}
              style={{ marginLeft: 5 }}
            />
          </div>

          {/* Inactive */}
          <div style={{ ...styles.formElement, display: "flex", flexDirection: "row" }}>
            <label style={styles.formLabel}>Inactive:</label>
            <input
              type="checkbox"
              checked={productData.inactive}
              onChange={(e) => setProductData({ ...productData, inactive: e.target.checked })}
              style={{ marginLeft: 5 }}
            />
          </div>

          {/* Ratings */}
          <div style={{ display: "flex", flexDirection: "row" }}>
            <div style={{ ...styles.formElement, marginRight: 20 }}>
              <label style={styles.formLabel}>Rating:</label>
              <input
                type="number"
                value={productData.rating_initial?.rating}
                onChange={(e) =>
                  setProductData({
                    ...productData,
                    rating_initial: {
                      ...productData.rating_initial,
                      rating: parseFloat(e.target.value) || 0,
                    },
                  })
                }
                style={{ ...styles.formInput, width: "100px" }}
                min="0"
                max="5"
                step="0.1"
              />
            </div>
            <div style={styles.formElement}>
              <label style={styles.formLabel}>Voting:</label>
              <input
                type="number"
                value={productData.rating_initial?.voting}
                onChange={(e) =>
                  setProductData({
                    ...productData,
                    rating_initial: {
                      ...productData.rating_initial,
                      voting: parseInt(e.target.value) || 0,
                    },
                  })
                }
                style={{ ...styles.formInput, width: "100px" }}
                min="0"
              />
            </div>
          </div>

          {/* Order Fields */}
          <div style={{ display: "flex", flexDirection: "row" }}>
            <div style={{ ...styles.formElement, marginRight: 20 }}>
              <label style={styles.formLabel}>Order Category Row:</label>
              <input
                type="number"
                value={productData.order_category_row}
                onChange={(e) => setProductData({ ...productData, order_category_row: e.target.value })}
                style={{ ...styles.formInput, width: "100px" }}
              />
            </div>
            <div style={styles.formElement}>
              <label style={styles.formLabel}>Order Category Page:</label>
              <input
                type="number"
                value={productData.order_category_page}
                onChange={(e) => setProductData({ ...productData, order_category_page: e.target.value })}
                style={{ ...styles.formInput, width: "100px" }}
              />
            </div>
          </div>

          {/* Staff Pick and Popular Product */}
          <div style={{ ...styles.formElement, display: "flex", flexDirection: "row", gap: "20px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <label style={styles.formLabel}>Staff Pick:</label>
              <input
                type="checkbox"
                checked={productData.staff_pick}
                onChange={(e) => setProductData({ ...productData, staff_pick: e.target.checked })}
                style={{ marginLeft: 5 }}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <label style={styles.formLabel}>Popular Product:</label>
              <input
                type="checkbox"
                checked={productData.popular_product}
                onChange={(e) => setProductData({ ...productData, popular_product: e.target.checked })}
                style={{ marginLeft: 5 }}
              />
            </div>
          </div>

          {/* Submit Button */}
          <div style={{ marginTop: "20px" }}>
            <button
              type="submit"
              style={{
                ...styles.button,
                backgroundColor: loading ? "silver" : "#478169",
              }}
              disabled={loading || !productData.brand_uid || !productData.product_name}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

const styles = {
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    width: "100%",
    border: "1px solid #ccc",
    borderRadius: 5,
    backgroundColor: "#f9f9f9",
  },
  formElement: {
    display: "flex",
    flexDirection: "column",
    marginBottom: 10,
  },
  formLabel: {
    marginBottom: 5,
    fontSize: 12,
    textTransform: "uppercase",
    color: "#4a4141",
  },
  formInput: {
    padding: "10px 15px",
    fontSize: 16,
    border: "0.5px solid #ccc",
    borderRadius: 5,
  },
  formSelect: {
    fontSize: 16,
    border: "0.5px solid #ccc",
    borderRadius: 5,
    height: 38,
    backgroundColor: "#fff",
  },
  button: {
    padding: "0px",
    width: "120px",
    color: "white",
    border: "none",
    borderRadius: 5,
    cursor: "pointer",
    fontSize: 14,
    height: 50,
  },
};

export default NewProduct;
